import { createAsyncThunk } from '@reduxjs/toolkit'
import { DirectUpload } from '@rails/activestorage'
import { FetchRequest } from '@rails/request.js'

import { camelKeys } from '../../utils/json'
import { FILE_CONTENT_TYPES } from 'bundles/Pilot/utils/files'

const PILOT_FILES_PATH = '/pilot/files.json'
const MAX_FILE_SIZE_MB = 25
const MAX_FILE_SIZE = 1024 * 1024 * MAX_FILE_SIZE_MB

interface DirectUploadBlob {
  signed_id: string
  [key: string]: any
}

type UploadFileParams = {
  attachment: File
  clientId: string
  threadKey: string
  onProgress?: (_progress: number) => void
}

export const uploadFile = createAsyncThunk(
  'files/uploadFile',
  async ({ attachment, clientId, threadKey, onProgress }: UploadFileParams) => {
    if (MAX_FILE_SIZE < attachment.size) {
      const message = `File if to large. Max size is ${MAX_FILE_SIZE_MB}MB.`

      console.error(message)
      throw new Error(message)
    } else if (!FILE_CONTENT_TYPES.find(type => attachment.type === type)) {
      const message = `File type (${attachment.type}) isn't allowed.`

      console.error(message)
      throw new Error(message)
    }

    const upload = new DirectUpload(attachment, '/rails/active_storage/direct_uploads', {
      directUploadWillStoreFileWithXHR: xhr => {
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            onProgress?.(progress)
          }
        })
      },
    })

    try {
      const blob: DirectUploadBlob = await new Promise((resolve, reject) => {
        upload.create((error, blob) => {
          if (error) {
            reject(new Error('Direct upload failed: ' + error))
          } else {
            resolve(blob as DirectUploadBlob)
          }
        })
      })

      const formData = new FormData()
      formData.append('pilot_file[attachment]', blob.signed_id)
      formData.append('pilot_file[client_id]', clientId)
      formData.append('pilot_file[thread_key]', threadKey)

      const request = new FetchRequest('POST', PILOT_FILES_PATH, {
        body: formData,
        responseKind: 'json',
      })

      const response = await request.perform()

      if (response.ok) {
        const json = await response.json
        return camelKeys(json)
      } else {
        throw new Error('File upload failed!\n\nPlease try again in a few moments.')
      }
    } catch (error) {
      console.error('Upload error:', error)
      throw new Error('File upload failed!\n\nPlease try again in a few moments.')
    }
  }
)
